import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { catchError, EMPTY, finalize, of, switchMap, tap, throwError } from 'rxjs';
import { buildCustomerOrderUpsertPayload } from './customer-order-upsert.payload';
import ProblemDetailsDto from '../../../../dto/error/problem-details-dto';
import { type SiteIdNameDto } from '../../../../dto/SiteIdNameDto';
import { type CustomerOrder, type Site } from '../../../../entities';
import { useNotification } from '../../../../hooks/use-notification';
import useSecuredApiV2 from '../../../../hooks/use-secured-api-v2';
import {
  CHECK_ORDERS_FOR_SITES_ENDPOINT_URL,
  CUSTOMER_ORDER_END_POINT_URL,
} from '../../../../utils/endpoints';
import { type LocalDate } from '../../../../utils/local-date';

const ORDERS_URL = '/orders';

interface OrderToSubmit {
  customerId: string;
  orderDate: LocalDate;
  /** The sites of the order, each carrying its site menu orders in "siteMenuOrders" */
  sites: Site[];
}

interface SubmitCustomerOrderCallbacks {
  /** Called once a submission ends, whether the order was saved or not */
  onSettled: () => void;
  /** Called once the order is saved, before leaving the page */
  onSaved: () => void;
}

interface SubmitCustomerOrderResult {
  isSubmitting: boolean;
  /** The sites which already have an order on the order date, waiting for the user to confirm */
  sitesWithExistingOrders: SiteIdNameDto[];
  isExistingOrdersWarningOpen: boolean;
  submit: (order: OrderToSubmit) => void;
  confirmExistingOrders: () => void;
  cancelExistingOrders: () => void;
}

/**
 * Submits "customerOrder": creates it, or updates it when it has an id. When some of its sites
 * already have an order on the same date, the submission waits for the user to confirm it.
 */
const useSubmitCustomerOrder = (
  customerOrder: CustomerOrder,
  { onSettled, onSaved }: SubmitCustomerOrderCallbacks
): SubmitCustomerOrderResult => {
  const api = useSecuredApiV2();
  const notification = useNotification();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [sitesWithExistingOrders, setSitesWithExistingOrders] = useState<SiteIdNameDto[]>([]);
  const [isExistingOrdersWarningOpen, setIsExistingOrdersWarningOpen] = useState(false);
  const pendingOrder = useRef<OrderToSubmit>();

  const notifyFailure = (error: ProblemDetailsDto): void => {
    notification.error({
      message: `Failed To ${
        customerOrder.id ? `Update Order #${customerOrder.id}` : 'Create Order'
      }`,
      description: error.detail ?? '',
    });
  };

  // The server builds the whole order graph: creating posts it, updating puts it and the server
  // appends a new revision.
  const save = ({ customerId, orderDate, sites }: OrderToSubmit): void => {
    const payload = buildCustomerOrderUpsertPayload(
      sites,
      customerId,
      orderDate,
      customerOrder.latestRevisionId
    );
    const request$ = customerOrder.id
      ? api.put(`${CUSTOMER_ORDER_END_POINT_URL}/${customerOrder.id}`, payload, {}, {})
      : api.post(CUSTOMER_ORDER_END_POINT_URL, payload, {}, {});

    setIsSubmitting(true);
    request$
      .pipe(
        switchMap((response) =>
          response.data ? of(response.data) : throwError(() => new ProblemDetailsDto(response))
        ),
        tap(() => {
          onSaved();
          notification.success({
            message: ' ',
            description: `Order ${
              customerOrder.id ? `#${customerOrder.id} updated` : 'submitted'
            } successfully!`,
          });
          navigate(ORDERS_URL);
        }),
        catchError((error: ProblemDetailsDto) => {
          notifyFailure(error);
          return EMPTY;
        }),
        finalize(() => {
          setIsSubmitting(false);
          onSettled();
        })
      )
      .subscribe();
  };

  const submit = (order: OrderToSubmit): void => {
    const siteIds = order.sites
      .filter((site) => (site.siteMenuOrders?.length ?? 0) > 0)
      .map((site) => site.id);
    if (siteIds.length === 0) {
      notification.error({ message: ' ', description: 'Please select at least one meal' });
      onSettled();
      return;
    }
    setIsSubmitting(true);
    // Check the sites which already have an order on the same date
    api
      .post(
        CHECK_ORDERS_FOR_SITES_ENDPOINT_URL,
        { sites: siteIds, customerOrderId: customerOrder.id },
        { orderDate: order.orderDate.toString() },
        {}
      )
      .pipe(
        switchMap((response) =>
          response.data ? of(response.data) : throwError(() => new ProblemDetailsDto(response))
        ),
        tap((sites: SiteIdNameDto[]) => {
          setSitesWithExistingOrders(sites);
          if (sites.length === 0) {
            save(order);
          } else {
            pendingOrder.current = order;
            setIsExistingOrdersWarningOpen(true);
          }
        }),
        catchError((error: ProblemDetailsDto) => {
          notifyFailure(error);
          return EMPTY;
        }),
        finalize(() => {
          setIsSubmitting(false);
        })
      )
      .subscribe();
  };

  const confirmExistingOrders = (): void => {
    setIsExistingOrdersWarningOpen(false);
    if (pendingOrder.current) save(pendingOrder.current);
  };

  const cancelExistingOrders = (): void => {
    setIsExistingOrdersWarningOpen(false);
  };

  return {
    isSubmitting,
    sitesWithExistingOrders,
    isExistingOrdersWarningOpen,
    submit,
    confirmExistingOrders,
    cancelExistingOrders,
  };
};

export default useSubmitCustomerOrder;
